import { useMemo } from "react";
import { Plus, Search, SlidersHorizontal, PanelLeftClose, Database, ChevronDown, RefreshCw, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useDatabaseConnection } from "@/contexts/DatabaseConnectionContext";
import { useConversation } from "@/contexts/ConversationContext";
import { useTableScanStatus } from "@/hooks/useTableScanStatus";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface ConversationSidebarProps {
  className?: string;
}

// DytaFly Logo Component
const DytaFlyLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="currentColor"/>
  </svg>
);

export function ConversationSidebar({ className }: ConversationSidebarProps) {
  const navigate = useNavigate();
  const {
    connections,
    selectedConnectionId,
    selectConnection,
  } = useDatabaseConnection();

  const {
    conversations,
    currentConversation,
    createConversation,
    selectConversation,
  } = useConversation();

  const selectedConnection = connections.find(c => c.id === selectedConnectionId);

  // Get table scan status for selected connection
  const {
    total: tableScanTotal,
    scanned: tableScanScanned,
    hasUnscanned,
    scanningInProgress,
    rescanAll,
    isComplete: isScanComplete,
  } = useTableScanStatus(selectedConnectionId || undefined);

  const handleScanDatabase = async () => {
    if (!selectedConnectionId) return;
    try {
      await rescanAll();
      toast.success('Database scan started');
    } catch (error) {
      toast.error('Failed to start database scan');
    }
  };

  // Group conversations by time
  const groupedConversations = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    const groups = {
      today: [] as typeof conversations,
      yesterday: [] as typeof conversations,
      lastWeek: [] as typeof conversations,
      older: [] as typeof conversations,
    };

    conversations.forEach((conv) => {
      const convDate = new Date(conv.created_at);
      if (convDate >= today) {
        groups.today.push(conv);
      } else if (convDate >= yesterday) {
        groups.yesterday.push(conv);
      } else if (convDate >= lastWeek) {
        groups.lastWeek.push(conv);
      } else {
        groups.older.push(conv);
      }
    });

    return groups;
  }, [conversations]);

  const handleNewChat = async () => {
    if (!selectedConnectionId) {
      toast.error("Please select a database connection first");
      return;
    }

    try {
      const newConv = await createConversation({
        db_connection_id: selectedConnectionId,
        title: "New Conversation",
      });
      if (newConv) {
        selectConversation(newConv);
      }
    } catch (error) {
      toast.error("Failed to create new conversation");
    }
  };

  const handleConversationClick = (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    selectConversation(conversation || null);
  };

  const renderConversationGroup = (
    title: string,
    conversationList: typeof groupedConversations.today
  ) => {
    if (conversationList.length === 0) return null;

    return (
      <div className="mt-4">
        <div className="px-5 mb-2">
          <span className="text-[11px] font-medium tracking-wider text-[#22212d]/50 uppercase">
            {title}
          </span>
        </div>
        <div className="flex flex-col">
          {conversationList.map((conv) => {
            const isActive = currentConversation?.id === conv.id;
            return (
              <div
                key={conv.id}
                onClick={() => handleConversationClick(conv.id)}
                className={cn(
                  "mx-3 px-2 py-1.5 cursor-pointer rounded transition-all",
                  isActive
                    ? "bg-[#ebebf0] border border-[#d8d8de]"
                    : "hover:bg-[#f3f3f6]"
                )}
              >
                <p className="text-[13px] font-normal text-[#22212d] truncate leading-5">
                  {conv.title || "Untitled Conversation"}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      className={cn(
        "w-[216px] bg-[#fcfcfd] border-r border-[#e6e6ea] flex flex-col shrink-0",
        className
      )}
    >
      {/* Logo Header */}
      <div className="px-4 pt-5 pb-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-[#6d2afe]">
              <DytaFlyLogo />
            </div>
            <span className="text-[15px] font-semibold text-[#22212d]">DytaFly</span>
          </div>
          <button className="p-1 rounded hover:bg-[#f3f3f6] text-[#22212d]/60 hover:text-[#22212d] transition-colors">
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Database Selector */}
      <div className="px-4 pb-3 shrink-0">
        <Select value={selectedConnectionId || ''} onValueChange={selectConnection}>
          <SelectTrigger className="h-9 px-3 bg-[#f3f3f6] border-0 hover:bg-[#ebebf0] focus:ring-1 focus:ring-[#6d2afe]">
            <SelectValue placeholder="Select Database">
              {selectedConnection ? (
                <div className="flex items-center gap-2">
                  <Database className="h-3.5 w-3.5 text-[#22212d]/70" />
                  <span className="text-[13px] font-medium text-[#22212d]">
                    {selectedConnection.alias}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Database className="h-3.5 w-3.5 text-[#22212d]/50" />
                  <span className="text-[13px] font-medium text-[#22212d]/50">
                    Select Database
                  </span>
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-white border-[#e6e6ea]">
            {connections.map((conn) => (
              <SelectItem key={conn.id} value={conn.id || ''} className="text-[13px]">
                <div className="flex items-center gap-2">
                  <Database className="h-3.5 w-3.5" />
                  <span>{conn.alias}</span>
                </div>
              </SelectItem>
            ))}
            <div className="border-t border-[#e6e6ea] mt-2 pt-2">
              <div
                onClick={() => navigate('/connections')}
                className="flex items-center gap-2 px-2 py-1.5 text-[#6d2afe] text-[13px] cursor-pointer hover:bg-[#f3f3f6] rounded"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Add New Connection</span>
              </div>
            </div>
          </SelectContent>
        </Select>

        {/* Scan Status Indicator */}
        {selectedConnectionId && tableScanTotal > 0 && (
          <div className="mt-2">
            {scanningInProgress ? (
              <div className="flex items-center gap-2 px-2 py-1.5 bg-blue-50 rounded text-blue-700">
                <RefreshCw className="h-3 w-3 animate-spin" />
                <span className="text-[11px]">Scanning {tableScanTotal - tableScanScanned} tables...</span>
              </div>
            ) : hasUnscanned ? (
              <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-amber-50 rounded">
                <div className="flex items-center gap-1.5 text-amber-700">
                  <AlertCircle className="h-3 w-3" />
                  <span className="text-[11px]">{tableScanScanned}/{tableScanTotal} tables ready</span>
                </div>
                <button
                  onClick={handleScanDatabase}
                  className="text-[11px] font-medium text-amber-700 hover:text-amber-900 underline"
                >
                  Scan
                </button>
              </div>
            ) : isScanComplete ? (
              <div className="flex items-center gap-1.5 px-2 py-1 text-green-700">
                <CheckCircle className="h-3 w-3" />
                <span className="text-[11px]">{tableScanTotal} tables ready</span>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Action Buttons Row */}
      <div className="px-4 pb-4 shrink-0">
        <div className="flex items-center gap-2">
          <button className="w-7 h-7 flex items-center justify-center rounded bg-[#f3f3f6] hover:bg-[#ebebf0] text-[#22212d]/70 hover:text-[#22212d] transition-colors">
            <Search className="h-3.5 w-3.5" />
          </button>
          <button className="w-7 h-7 flex items-center justify-center rounded bg-[#f3f3f6] hover:bg-[#ebebf0] text-[#22212d]/70 hover:text-[#22212d] transition-colors">
            <SlidersHorizontal className="h-3.5 w-3.5" />
          </button>
          <Button
            onClick={handleNewChat}
            className="flex-1 h-7 px-3 text-[13px] font-medium bg-[#6d2afe] hover:bg-[#5a1ee6] text-white border-0"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New Chat
          </Button>
        </div>
      </div>

      {/* Conversation History */}
      <ScrollArea className="flex-1">
        <div className="pb-4">
          {renderConversationGroup("TODAY", groupedConversations.today)}
          {renderConversationGroup("YESTERDAY", groupedConversations.yesterday)}
          {renderConversationGroup("LAST 7 DAYS", groupedConversations.lastWeek)}
          {renderConversationGroup("OLDER", groupedConversations.older)}

          {/* Empty state */}
          {conversations.length === 0 && (
            <div className="mt-8 text-center px-4">
              <p className="text-[13px] text-[#22212d]/50">
                No conversations yet
              </p>
              <p className="text-[12px] text-[#22212d]/30 mt-1">
                Click "New Chat" to start
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default ConversationSidebar;
